import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart } from './BarChart';
import { useFiltrosDashboardPichincha } from '../../../store/useFiltersDashboardPichincha';
import { useDisponibilidadISP } from '../../../hooks/useDisponibilidadIsp';

export function AvailabilityChart() {
  const { t } = useTranslation();
  const globalStatePeriod = useFiltrosDashboardPichincha(
    (state) => state.globalStatePeriod
  );
  const objetivo = useFiltrosDashboardPichincha((state) => state.objetivo);
  const { data, isLoading, isError } = useDisponibilidadISP({
    objetivoId: objetivo?.objetivo_id.toString() ?? null,
    fechaInicio: globalStatePeriod.startDate,
    fechaTermino: globalStatePeriod.endDate,
    enabled: Boolean(objetivo?.objetivo_id),
  });
  const [checkedNodos, setCheckedNodos] = React.useState([]);

  const processedData = React.useMemo(() => {
    let chartSerieUp = [];
    let chartSerieDown = [];
    let chartSerieNoMon = [];
    let chartCategory = [];

    if (!data || !data.length) {
      return {
        chartSerieUp: [],
        chartSerieDown: [],
        chartSerieNoMon: [],
        chartCategory: [],
      };
    }

    for (const item of data) {
      if (checkedNodos.includes(item.nodo_id)) {
        continue; // Skip nodes that are checked
      }
      chartCategory.push(item.nodo_nombre);
      chartSerieUp.push(
        parseFloat(
          (
            item.porcentaje_uptime +
            item.porcentaje_eventos_cliente +
            item.porcentaje_marcados_atentus
          ).toFixed(2)
        )
      );
      chartSerieDown.push(parseFloat(item.porcentaje_downtime.toFixed(2)));
      chartSerieNoMon.push(
        parseFloat(item.porcentaje_no_monitoreos.toFixed(2))
      );
    }
    return {
      chartSerieUp,
      chartSerieDown,
      chartSerieNoMon,
      chartCategory,
    };
  }, [data, checkedNodos]);

  if (!objetivo) {
    return null;
  }

  if (!isLoading && !data && !isError) {
    return (
      <Alert severity="error">
        {t('sync.availability.chart.error.noData')}
      </Alert>
    );
  }

  if (!isLoading && isError) {
    return (
      <Alert severity="error">
        {t('sync.availability.chart.error.loadNodes')}
      </Alert>
    );
  }

  const { chartSerieUp, chartSerieDown, chartSerieNoMon, chartCategory } =
    processedData;

  function handleChangeCheckbox(event) {
    const nodoId = parseInt(event.target.value, 10);
    if (event.target.checked) {
      setCheckedNodos((prev) => prev.filter((id) => id !== nodoId));
    } else {
      setCheckedNodos((prev) => [...prev, nodoId]);
    }
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
        {!isLoading && (
          <Box marginBottom={2}>
            {data.map((item) => (
              <FormControlLabel
                key={item.nodo_id}
                label={
                  <Typography variant="body2">{item.nodo_nombre}</Typography>
                }
                control={
                  <Checkbox
                    checked={!checkedNodos.includes(item.nodo_id)}
                    value={item.nodo_id}
                    onChange={handleChangeCheckbox}
                    size="small"
                    color='primary'
                  />
                }
              />
            ))}
          </Box>
        )}
        <BarChart
          chartCategory={chartCategory}
          chartSerieUp={chartSerieUp}
          chartSerieDown={chartSerieDown}
          chartSerieNoMon={chartSerieNoMon}
          title={t('sync.availability.chart.label.nodes')}
          isLoading={isLoading}
        />
      </Grid>
    </Grid>
  );
}
